
import { action, runInAction, observable } from 'mobx';
import cloneDeep from 'lodash/cloneDeep';
import { ErrorInterface } from 'modules/core/api/globalApi';
import schedulerController from './controller';
import {
  SchedulerInterface,
  SchedulerRequestInterface,
  AppointmentInterface,
  AppointmentOneInterface,
  SchedulerPostRequestInterface,
  SchedulerPutRequestInterface,
  MoveAppointmentInterface,
  CreateOrderByAppointmentByIdResponse,
  AvailableDatesRequestInterface,
  AvailableTimesRequestInterface,
  AvailableTimeItemInterface,
  AppointmentDetailsInterface,
  DeletedAppointmentInterface,
  DeletedAppointmentRequestInterface,
  FoundAppointmentItemInterface,
  FoundAppointmentRequestInterface,
  AppointmentHistoryItemInterface,
  AvailableDatesByOfficeRequestInterface,
  AvailableTimesByOfficeRequestInterface,
  AppointmentFileItemInterface,
  TimelineInterface,
  TimelineRequestInterface,
  TimelineAppointmentInterface,
  SchedulerStretchRequestInterface,
  AppointmentExecutorsRequest,
  FifthWheelAppointmentsRequestInterface,
  FifthWheelAppointmentItemInterface,
} from '../api/schedulerApi';
import { MultiSelectedOfficeItemIntreface } from 'modules/partners/store/offices/officesApi';

const LOCAL_STORAGE_KEYS = {
  SELECTED_TIMELINE_PARTNER_OFFICE_IDS: 'selectedTimelinePartnerOfficeIds',
};

export interface SchedulerStore {
  scheduler: SchedulerInterface,
  schedulerRequest: SchedulerRequestInterface,
  loadingScheduler: boolean,
  currentAppointment: AppointmentInterface,
  fullCurrentAppointment: AppointmentOneInterface, // для формы редактирования храним тут детальную информацию о записи
  error: ErrorInterface,
  // настройки состояния формы расписания (вариант на 1 день, вертикальное время):
  selectedServiceId: number,
  selectedPartnerId: string,
  selectedPartnerOfficeId: string,
  // Todo - сделал временно any, бэк не готов. Точные форматы не известны

  // настройки состояния формы расписания (вариант Timeline на насколько дней, вертикально офисы):
  selectedTimelineServiceId: number,
  selectedTimelinePartnerId: string,
  selectedTimelinePartnerOfficeId: string,
  selectedTimelinePartnerOfficeIds: MultiSelectedOfficeItemIntreface[],
  selectedTimelineSchedulerClassifierRefValueId: number,
  // selectedTimelineSinceDate: string,
  // selectedTimelineTillDate: string,

  availableDates: string[],

  availableTimes: AvailableTimeItemInterface[],

  deletedAppointments: DeletedAppointmentInterface[],
  deletedAppointmentRequest: DeletedAppointmentRequestInterface,
  messageInfo: string

  foundAppointments: FoundAppointmentItemInterface[],
  foundAppointmentsRequest: FoundAppointmentRequestInterface,

  fifthWheelAppointments: FifthWheelAppointmentItemInterface[],
  fifthWheelAppointmentsRequest: FifthWheelAppointmentsRequestInterface,
  loadingFifthWheelAppointments: boolean,
  cancellingFifthWheelAppointmentId: string,

  appointmentHistory: AppointmentHistoryItemInterface[],
  appointmentHistoryById: string,

  fileList: AppointmentFileItemInterface[],
  fileListByAppointmentId: string

  timeline: TimelineInterface,
  timelineRequest: TimelineRequestInterface,

  // true, когда диаметр колёс в форме записи поставлен автоопределением по авто
  // (см. SchedulerModalMainPanel), а не пришёл из записи и не выбран руками.
  // Используется SchedulerTireFittingPanel, чтобы не навязывать авто-расчёт цены
  // для существующих записей и при ручном выборе диаметра.
  wheelDiameterAutoDetected: boolean,
}

const initialValues: SchedulerStore = {
  scheduler: {
    appointmentDefaultDuration: 30,
    appointmentGridStep: 30,
    appointments: [],
    startDayHour: 9, // TODO - может числом сделать ?
    endDayHour: 18,
    posts: [],
  },
  schedulerRequest: null,
  currentAppointment: null,
  fullCurrentAppointment: null,
  loadingScheduler: false,
  error: null,
  selectedServiceId: null,
  selectedPartnerId: null,
  selectedPartnerOfficeId: null,

  selectedTimelineServiceId: null, // выставляется при монтировании SchedulerTimeline из servicesList роута
  selectedTimelinePartnerId: null,
  selectedTimelinePartnerOfficeId: null,
  selectedTimelinePartnerOfficeIds: (() => {
    // Загружаем из localStorage при инициализации
    try {
      const stored = localStorage.getItem(LOCAL_STORAGE_KEYS.SELECTED_TIMELINE_PARTNER_OFFICE_IDS);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  })(),
  selectedTimelineSchedulerClassifierRefValueId: null,

  availableDates: [],
  availableTimes: [],

  deletedAppointments: [],
  deletedAppointmentRequest: null,

  messageInfo: null,

  foundAppointments: [],
  foundAppointmentsRequest: null,

  fifthWheelAppointments: [],
  fifthWheelAppointmentsRequest: null,
  loadingFifthWheelAppointments: false,
  cancellingFifthWheelAppointmentId: null,

  appointmentHistory: [],
  appointmentHistoryById: null,

  fileList: [],
  fileListByAppointmentId: null,

  timeline: {
    appointmentDefaultDuration: 30,
    appointmentGridStep: 30,
    startDayHour: 9,
    endDayHour: 18,
    /// appointments: [],
    posts: [],
    appointments: [],
    nonWorkingHours: [],
  },
  timelineRequest: null,

  wheelDiameterAutoDetected: false,

  // selectedTimelineSinceDate: undefined,
  // selectedTimelineTillDate: undefined,
};
export const schedulerStore = observable(initialValues);

export const schedulerActions = {

  handleError: action((response) => {
    if (response.errorData) {
      const { errorData } = response;
      schedulerStore.error = errorData;
      // TODO  - при НЕ возможности сделать временный резерв не надо очищать планировщик
      // возможно, надо сделать отдельный метод handleError и вызывать его из createAppointmentByDateTime
      // schedulerActions.clearScheduler();
      schedulerStore.loadingScheduler = false;
      throw errorData;
    }
  }),

  handleErrorWithoutThrow: action((response) => {
    if (response.errorData) {
      const { errorData } = response;
      schedulerStore.loadingScheduler = false;
      schedulerStore.error = errorData;
    }
  }),

  clearError: action(() => {
    schedulerStore.error = null;
    schedulerStore.messageInfo = null;
  }),

  // Возвращает полный объект с расписанием
  async getSchedulerByDate(request: SchedulerRequestInterface) {
    schedulerActions.clearError();
    runInAction(() => {
      schedulerStore.schedulerRequest = request;
      schedulerStore.loadingScheduler = true;
    });
    const response = await schedulerController.getSchedulerByDate(request);
    schedulerActions.handleError(response);
    runInAction(() => {
      schedulerStore.scheduler = response.data;
      schedulerStore.loadingScheduler = false;
    });
  },

  // Возвращает полный объект с расписанием
  async getOneAppointment(appointmentJournalId: string) {
    schedulerActions.clearError();
    runInAction(() => {
      // schedulerStore.schedulerRequest = request;
      schedulerStore.loadingScheduler = true;
    });
    const response = await schedulerController.getOneAppointment(appointmentJournalId);
    schedulerActions.handleError(response);
    runInAction(() => {
      schedulerStore.fullCurrentAppointment = response.data;
      schedulerStore.loadingScheduler = false;
    });
  },

  async getOneAppointmentAndSetCurrentAppointment(appointmentJournalId: string) {
    schedulerActions.clearError();
    runInAction(() => {
      // schedulerStore.schedulerRequest = request;
      schedulerStore.loadingScheduler = true;
    });
    const response = await schedulerController.getOneAppointment(appointmentJournalId);    
    schedulerActions.handleError(response);
    runInAction(() => {
      schedulerStore.fullCurrentAppointment = response.data;
      schedulerStore.currentAppointment = response.data;
      schedulerStore.loadingScheduler = false;
    });
  },

  async clearScheduler() {
    try {
      runInAction(() => {
        schedulerStore.scheduler = { ...initialValues.scheduler };
        schedulerStore.loadingScheduler = false;
        schedulerStore.schedulerRequest = null;
        schedulerStore.messageInfo = null;
      });
    } catch (err) {
      this.handleError(err);
    }
  },

  setCurrentAppointment: action((appointment: AppointmentInterface) => {
    if (schedulerStore?.fullCurrentAppointment?.id !== appointment?.id) {
      schedulerStore.fullCurrentAppointment = null;
    }
    schedulerStore.currentAppointment = cloneDeep(appointment);
  }),

  setFindAndSetCurrentAppointmentByTimeline: action((id: string) => {
    // const x: AppointmentInterface = schedulerStore.timeline.appointments?.find((el) => el.id === id);
    const y: TimelineAppointmentInterface= schedulerStore.timeline.appointments?.find((el) => el.id === id);
    // if (schedulerStore?.fullCurrentAppointment?.id !== appointment?.id) {
    //   schedulerStore.fullCurrentAppointment = null;
    // }
    schedulerStore.currentAppointment = {
      ...y,
      // comment?: y?.comment,
      endDate: y?.end,
      officePostId: y?.resourceId,
      startDate: y?.start,
      // tireOperationRefValueName?: string,
      // isCorporateVehicle?: boolean,
      // wheelDeliveryStatusRefValueId?: number,
      // color?: string,
    };
  }),

  setFullAppointment: action((appointment: AppointmentOneInterface) => {
    schedulerStore.fullCurrentAppointment = cloneDeep(appointment);
  }),

  clearCurrentAppointment: action(() => {
    schedulerStore.fullCurrentAppointment = null;
    schedulerStore.currentAppointment = null;
    schedulerStore.messageInfo = null;
    schedulerStore.wheelDiameterAutoDetected = false;
  }),

  setWheelDiameterAutoDetected: action((value: boolean) => {
    schedulerStore.wheelDiameterAutoDetected = value;
  }),

  // Создаёт временный резерв и сразу добавляет в планировщик
  async createAppointmentByDateTime(request: SchedulerPostRequestInterface, setReserveToTimeline: boolean = false) {
    schedulerActions.clearError();
    runInAction(() => {
      schedulerStore.loadingScheduler = true;
    });

    const response = await schedulerController.createAppointmentByDateTime(request);
  
    // Сохраняем оригинальное поведение: выбрасываем ошибку при errorData
    schedulerActions.handleError(response);

    // 🟢 Оптимизированное обновление состояния
    runInAction(() => {
      const newAppointment = { serviceId: request.serviceId, ...response.data };
    
      if (setReserveToTimeline) {
      // Обновляем timeline: новый объект + новый массив назначений
        schedulerStore.timeline = {
          ...schedulerStore.timeline,
          appointments: [...schedulerStore.timeline.appointments, {
            id: response.data.id,
            start: response.data.startDate,
            end: response.data.endDate,
            resourceId: response.data.officePostId,
            title: response.data.title,
          }],
        };
      } else {
      // Обновляем scheduler: новый объект + новый массив назначений
        schedulerStore.scheduler = {
          ...schedulerStore.scheduler,
          appointments: [...schedulerStore.scheduler.appointments, newAppointment],
        };
      }
    
      schedulerStore.loadingScheduler = false;
    });

    // Устанавливаем текущую запись (как в оригинале)
    schedulerActions.setCurrentAppointment({ ...response.data });
  },

  createParsedDetails(s: string): AppointmentDetailsInterface {
    return {
      ...JSON.parse(s ?? '{}'),
    };
  },

  // Изменяет запись в планировщике
  async updateAppointmentById(
    appointmentJournalId: string, 
    request: SchedulerPutRequestInterface, 
    updateTimeline: boolean = false
  ) {
    schedulerActions.clearError();
    runInAction(() => {
      schedulerStore.loadingScheduler = true;
    });

    const response = await schedulerController.updateAppointmentById(appointmentJournalId, request);
  
    // Сохраняем оригинальное поведение: выбрасываем ошибку при errorData
    schedulerActions.handleError(response);

    // 🟢 Оптимизированное обновление состояния
    runInAction(() => {
      const parsedDetails: AppointmentDetailsInterface = schedulerActions
        .createParsedDetails(response?.data?.item?.details ?? {});
    
      const updatedAppointment = {
        ...response.data.item,
        ...parsedDetails,
      };

      if (updateTimeline) {
      // Обновляем timeline: новый объект + обновлённый массив назначений
        schedulerStore.timeline = {
          ...schedulerStore.timeline,
          appointments: schedulerStore.timeline.appointments.map((item) =>
            item.id === appointmentJournalId
              ? {
                id: response.data.item.id,
                start: response.data.item.startDate,
                end: response.data.item.endDate,
                resourceId: response.data.item.officePostId,
                title: response.data.item.title,
                ...parsedDetails,
              }
              : item
          ),
        };
      } else {
      // Обновляем scheduler: новый объект + обновлённый массив назначений
        schedulerStore.scheduler = {
          ...schedulerStore.scheduler,
          appointments: schedulerStore.scheduler.appointments.map((item) =>
            item.id === appointmentJournalId ? updatedAppointment : item
          ),
        };
      }
    
      schedulerStore.messageInfo = response?.data?.message ?? null;
      schedulerStore.loadingScheduler = false;
    });
  },

  // Изменяет запись в планировщике - специальный метод для Drag&Drop
  async updateAppointmentByIdDragDrop(
    appointmentJournalId: string,
    request: SchedulerPutRequestInterface,
    moveRequest: MoveAppointmentInterface
  ) {
    schedulerActions.clearError();

    // 🟢 Сохраняем оригинал для отката, но БЕЗ cloneDeep — храним только нужные данные
    const originalAppointments = schedulerStore.scheduler.appointments;

    // 🟢 Оптимистичное обновление: мгновенно показываем перемещение
    runInAction(() => {
      schedulerStore.scheduler = {
        ...schedulerStore.scheduler,
        appointments: schedulerStore.scheduler.appointments.map((item) =>
          item.id === appointmentJournalId
            ? { ...item, ...moveRequest } // Применяем только изменения из moveRequest
            : item
        ),
      };
      schedulerStore.loadingScheduler = true;
    });

    try {
      const response = await schedulerController.updateAppointmentById(appointmentJournalId, request);
    
      // Сохраняем оригинальное поведение: handleError выбрасывает при errorData
      schedulerActions.handleError(response);

      // 🟢 Успех: обновляем запись данными с бэка
      runInAction(() => {
        const parsedDetails: AppointmentDetailsInterface = schedulerActions
          .createParsedDetails(response?.data?.details ?? {});
      
        schedulerStore.scheduler = {
          ...schedulerStore.scheduler,
          appointments: schedulerStore.scheduler.appointments.map((item) =>
            item.id === appointmentJournalId
              ? { ...item, ...response.data, ...parsedDetails }
              : item
          ),
        };
        schedulerStore.loadingScheduler = false;
      });
    } catch (error) {
    // 🟢 Ошибка: откатываем к оригинальному состоянию
      runInAction(() => {
        schedulerStore.scheduler = {
          ...schedulerStore.scheduler,
          appointments: originalAppointments, // Восстанавливаем оригинальный массив
        };
        schedulerStore.loadingScheduler = false;
      });
      // Пробрасываем ошибку дальше (как в оригинале через handleError)
      throw error;
    }
  },

  // Изменяет запись в планировщике - продолжительность или/и время
  async stretchAppointmentById(
    appointmentJournalId: string,
    request: SchedulerStretchRequestInterface,
    moveRequest: MoveAppointmentInterface,
    isTimelineMode: boolean = false
  ) {

    // Далее запутанная логика - т.к. приходится сразу в 2 разных планировщиках править
    // TODO - Надо сделать отдельные функции для каждого планировщика и избавиться от isTimelineMode
    
    schedulerActions.clearError();
    const originalScheduler = cloneDeep(schedulerStore.scheduler);
    const originalTimeline = cloneDeep(schedulerStore.timeline);

    const x = cloneDeep(schedulerStore.scheduler);
    if (!isTimelineMode) {
    // сразу применим изменённую запись для корректного рендера
      x.appointments = x.appointments.filter((v) => v.id !== appointmentJournalId);
      x.appointments = [...x.appointments,
        {
          ...schedulerStore.scheduler.appointments.find((v) => v.id === appointmentJournalId),
          ...moveRequest, 
        }];
      schedulerStore.scheduler = cloneDeep(x);
    }

    //  а теперь начинаем менять в базе
    schedulerStore.loadingScheduler = true;
    const response = await schedulerController.stretchAppointmentById(appointmentJournalId, request);
    if (response.errorData) {
      runInAction(() => {
        if (isTimelineMode) {
          schedulerStore.timeline = cloneDeep(originalTimeline);
        }
        else {
          schedulerStore.scheduler = cloneDeep(originalScheduler); // при ошибки восстановим старые планировщик
        }
        schedulerStore.loadingScheduler = false;
      });
      schedulerActions.handleError(response);
    } else {
      x.appointments = x.appointments.filter((v) => v.id !== appointmentJournalId);
      const parsedDetails: AppointmentDetailsInterface = schedulerActions
        .createParsedDetails(response?.data?.details ?? {});
      x.appointments = [...x.appointments, {
        ...schedulerStore.scheduler.appointments.find((v) => v.id === appointmentJournalId),
        ...response.data,
        ...parsedDetails,
      }]; // перезапишем в планировщике полученную запись
      schedulerStore.scheduler = cloneDeep(x);
      schedulerStore.loadingScheduler = false;
    }
  },

  // Перенос записи в планировщике
  async moveAppointmentById(appointmentJournalId: string, request: MoveAppointmentInterface) {
    schedulerActions.clearError();

    // дубль
    const originalScheduler = cloneDeep(schedulerStore.scheduler);

    // сразу применим изменённую запись для корректного рендера
    const x = cloneDeep(schedulerStore.scheduler);
    x.appointments = x.appointments.filter((v) => v.id !== appointmentJournalId);
    x.appointments = [...x.appointments,
      { ...schedulerStore.scheduler.appointments.find((v) => v.id === appointmentJournalId), ...request }];
    schedulerStore.scheduler = cloneDeep(x);
    // конец дубля

    runInAction(() => {
      //        schedulerStore.schedulerRequest = request;
      schedulerStore.loadingScheduler = true;
    });

    const response = await schedulerController.moveAppointmentById(appointmentJournalId, {
      newOfficePostId: request.officePostId,
      newReserveDateTime: request.startDate,
    });
    
    if (response.errorData) {
      runInAction(() => {
        schedulerStore.scheduler = cloneDeep(originalScheduler); // при ошибки восстановим старые планировщик
        // schedulerStore.loadingScheduler = false;
      });
      schedulerActions.handleError(response);
    } else {
      x.appointments = x.appointments.filter((v) => v.id !== appointmentJournalId);
      x.appointments = [...x.appointments,
        { ...schedulerStore.scheduler.appointments.find((v) => v.id === appointmentJournalId), ...request }];
      runInAction(() => {
        schedulerStore.scheduler = cloneDeep(x);
        schedulerStore.loadingScheduler = false;
      });
    }
  },

  deleteAppointmentById: async (id: string, isTimelineMode: boolean): Promise<void> => {
    if (schedulerStore.error) { schedulerActions.clearError(); }
    const response = await schedulerController.deleteAppointmentById(id);
    schedulerActions.handleError(response);
    if (!response.errorData) {
      if (!isTimelineMode) {
        const x = cloneDeep(schedulerStore.scheduler);
        x.appointments = schedulerStore.scheduler.appointments.filter((v) => v.id !== id);
        runInAction(() => {
          schedulerStore.scheduler = cloneDeep(x);
        });
        schedulerActions.clearOneItemFromFoundAppointmentsById(id);
      }
      else {
        const x = cloneDeep(schedulerStore.timeline);
        x.appointments = schedulerStore.timeline.appointments.filter((v) => v.id !== id);
        runInAction(() => {
          schedulerStore.timeline = cloneDeep(x);
        });
        // schedulerActions.clearOneItemFromFoundAppointmentsById(id);
      }
    }
  },

  /* refreshScheduler: async (id: string): Promise<void> => {
    if (schedulerStore.error) { schedulerActions.clearError(); }
    const x = cloneDeep(schedulerStore.scheduler);
    x.appointments = schedulerStore.scheduler.appointments.filter(v => v.id !== id);
    runInAction(() => {
      schedulerStore.scheduler = cloneDeep(x);
    });
    runInAction(() => {
      schedulerStore.scheduler = cloneDeep(schedulerStore.scheduler);
    });
    schedulerActions.clearOneItemFromFoundAppointmentsById(id);
}, */

  createOrderByAppointmentById: async (id: string, request: AppointmentExecutorsRequest): 
    Promise<CreateOrderByAppointmentByIdResponse> => {
    if (schedulerStore.error) { schedulerActions.clearError(); }
    // CreateOrderByAppointmentByIdResponse
    const response = await schedulerController.createOrderByAppointmentById(id, request);
    schedulerActions.handleError(response);
    if (!response.errorData) {
      /* TODO - доделать логику после формирования заявки */
      schedulerActions.getOneAppointment(id);
    }
    return response?.data;
  },

  // блок для однодневного расписания
  setSelectedServiceId: action((serviceId: number) => {
    schedulerStore.selectedServiceId = serviceId;
  }),

  setSelectedPartnerId: action((partnerId: string) => {
    schedulerStore.selectedPartnerId = partnerId;
  }),

  setSelectedPartnerOfficeId: action((partnerOfficeId: string) => {
    schedulerStore.selectedPartnerOfficeId = partnerOfficeId;
  }),

  // Возвращает доступные даты для переноса записи  по записи
  async getAvailableDates(request: AvailableDatesRequestInterface) {
    schedulerActions.clearError();
    runInAction(() => {
      // schedulerStore.schedulerRequest = request;
      schedulerStore.loadingScheduler = true;
    });
    const response = await schedulerController.getAvailableDates(request);
    schedulerActions.handleError(response);
    runInAction(() => {
      schedulerStore.availableDates = response?.data?.items ?? [];
      schedulerStore.loadingScheduler = false;
    });
  },

  // Возвращает доступные даты для переноса записи  по офису
  async getAvailableDatesByOffice(request: AvailableDatesByOfficeRequestInterface) {
    schedulerActions.clearError();
    runInAction(() => {
      schedulerStore.loadingScheduler = true;
    });
    const response = await schedulerController.getAvailableDatesByOffice(request);
    schedulerActions.handleError(response);
    runInAction(() => {
      schedulerStore.availableDates = response?.data?.items ?? [];
      schedulerStore.loadingScheduler = false;
    });
  },

  // Возвращает доступное время для переноса записи по id
  async getAvailableTimes(request: AvailableTimesRequestInterface) {
    schedulerActions.clearError();
    runInAction(() => {
      // schedulerStore.schedulerRequest = request;
      schedulerStore.loadingScheduler = true;
    });
    const response = await schedulerController.getAvailableTimes(request);
    schedulerActions.handleError(response);
    runInAction(() => {
      schedulerStore.availableTimes = response?.data?.items ?? [];
      schedulerStore.loadingScheduler = false;
    });
  },

  // Возвращает доступное время для переноса записи по офису
  async getAvailableTimesByOffice(request: AvailableTimesByOfficeRequestInterface) {
    schedulerActions.clearError();
    runInAction(() => {
      // schedulerStore.schedulerRequest = request;
      schedulerStore.loadingScheduler = true;
    });
    const response = await schedulerController.getAvailableTimesByOffice(request);
    schedulerActions.handleError(response);
    runInAction(() => {
      schedulerStore.availableTimes = response?.data?.items ?? [];
      schedulerStore.loadingScheduler = false;
    });
  },

  async getDeletedAppointments(request: DeletedAppointmentRequestInterface) {
    schedulerActions.clearError();
    schedulerActions.clearDeletedAppointments();
    runInAction(() => {
      // schedulerStore.schedulerRequest = request;
      schedulerStore.loadingScheduler = true;
    });
    const response = await schedulerController.getDeletedAppointments(request);
    schedulerActions.handleError(response);
    runInAction(() => {
      schedulerStore.deletedAppointments = response?.data ?? [];
      schedulerStore.loadingScheduler = false;
    });
  },

  async clearDeletedAppointments() {
    try {
      runInAction(() => {
        schedulerStore.deletedAppointments = [];
        schedulerStore.deletedAppointmentRequest = null;
      });
    } catch (err) {
      this.handleError(err);
    }
  },

  async getFoundAppointments(request: FoundAppointmentRequestInterface) {
    schedulerActions.clearError();
    schedulerActions.clearFoundAppointments();
    runInAction(() => {
      schedulerStore.foundAppointmentsRequest = request;
    });
    const response = await schedulerController.getFoundAppointments(request);
    schedulerActions.handleError(response);
    runInAction(() => {
      schedulerStore.foundAppointments = response?.data?.items ?? [];
    });
  },

  async clearFoundAppointments() {
    try {
      runInAction(() => {
        schedulerStore.foundAppointments = [];
        schedulerStore.foundAppointmentsRequest = null;
      });
    } catch (err) {
      this.handleError(err);
    }
  },

  // Журнал записей «5 колеса»: загрузка без throw — ошибка уходит в schedulerStore.error
  async getFifthWheelAppointments(request: FifthWheelAppointmentsRequestInterface) {
    schedulerActions.clearError();
    runInAction(() => {
      schedulerStore.fifthWheelAppointmentsRequest = request;
      schedulerStore.loadingFifthWheelAppointments = true;
    });
    const response = await schedulerController.getFifthWheelAppointments(request);
    runInAction(() => {
      if (response.errorData || response.error || response.connectError) {
        schedulerStore.error = response.errorData
          ?? (typeof response.data?.message === 'string' ? { message: response.data.message } : null);
        schedulerStore.fifthWheelAppointments = [];
      } else {
        schedulerStore.fifthWheelAppointments = response?.data?.items ?? [];
      }
      schedulerStore.loadingFifthWheelAppointments = false;
    });
  },

  // Отмена записи «5 колеса»: обновляем строку списком на месте, без перезагрузки;
  // возвращает true при успехе (страница показывает toastr)
  async cancelFifthWheelAppointment(appointmentId: string): Promise<boolean> {
    schedulerActions.clearError();
    runInAction(() => {
      schedulerStore.cancellingFifthWheelAppointmentId = appointmentId;
    });
    const response = await schedulerController.cancelFifthWheelAppointment(appointmentId);
    const ok = !response.errorData && !response.error && !response.connectError;
    runInAction(() => {
      if (ok) {
        schedulerStore.fifthWheelAppointments = schedulerStore.fifthWheelAppointments
          .map((item) => (item.id === appointmentId ? response.data : item));
      } else {
        schedulerStore.error = response.errorData
          ?? (typeof response.data?.message === 'string' ? { message: response.data.message } : null);
      }
      schedulerStore.cancellingFifthWheelAppointmentId = null;
    });
    return ok;
  },

  async clearOneItemFromFoundAppointmentsById(id: string) {
    try {
      runInAction(() => {
        schedulerStore.foundAppointments = schedulerStore.foundAppointments.filter((v) => v.id !== id);
      });
    } catch (err) {
      this.handleError(err);
    }
  },

  async getAppointmentHistory(appointmentJournalId: string) {
    schedulerActions.clearError();
    schedulerActions.clearAppointmentHistory();
    runInAction(() => {
      schedulerStore.appointmentHistoryById = appointmentJournalId;
    });
    const response = await schedulerController.getAppointmentHistory(appointmentJournalId);
    schedulerActions.handleError(response);
    runInAction(() => {
      schedulerStore.appointmentHistory = response?.data ?? [];
    });
  },

  async getDeletedAppointmentHistory(deletedAppointmentJournalId: string) {
    schedulerActions.clearError();
    schedulerActions.clearAppointmentHistory();
    runInAction(() => {
      schedulerStore.appointmentHistoryById = deletedAppointmentJournalId;
    });
    const response = await schedulerController.getDeletedAppointmentHistory(deletedAppointmentJournalId);
    schedulerActions.handleError(response);
    runInAction(() => {
      schedulerStore.appointmentHistory = response?.data ?? [];
    });
  },

  async clearAppointmentHistory() {
    try {
      runInAction(() => {
        schedulerStore.appointmentHistory = [];
        schedulerStore.appointmentHistoryById = null;
      });
    } catch (err) {
      this.handleError(err);
    }
  },

  async getFiles(appointmentId: string) {
    schedulerActions.clearError();
    schedulerActions.clearFiles();
    runInAction(() => {
      schedulerStore.fileListByAppointmentId = appointmentId;
    });
    const response = await schedulerController.getFiles(appointmentId);
    schedulerActions.handleError(response);
    runInAction(() => {
      schedulerStore.fileList = response?.data ?? [];
    });
  },

  async clearFiles() {
    try {
      runInAction(() => {
        schedulerStore.fileList = [];
        schedulerStore.fileListByAppointmentId = null;
      });
    } catch (err) {
      this.handleError(err);
    }
  },

  async addAppointmentFile(data: any) {
    schedulerActions.clearError();
    const response = await schedulerController.addAppointmentFile(data);
    schedulerActions.handleError(response);
  },

  deleteAppointmentFile: async (fileId: string): Promise<void> => {
    if (schedulerStore.error) { schedulerActions.clearError(); }
    const response = await schedulerController.deleteAppointmentFile(fileId);
    schedulerActions.handleError(response);
    if (!response.errorData) {
      runInAction(() => {
        schedulerStore.fileList = schedulerStore.fileList.filter((v) => v.id !== fileId);
      });
    }
  },

  // блок для Timeline

  setSelectedTimelineServiceId: action((serviceId: number) => {
    schedulerStore.selectedTimelineServiceId = serviceId;
  }),

  setSelectedTimelinePartnerId: action((partnerId: string) => {
    schedulerStore.selectedTimelinePartnerId = partnerId;
  }),

  setSelectedTimelinePartnerOfficeId: action((partnerOfficeId: string) => {
    schedulerStore.selectedTimelinePartnerOfficeId = partnerOfficeId;
  }),

  setSelectedTimelinePartnerOfficeIds: action((partnerOfficeIds: MultiSelectedOfficeItemIntreface[]) => {    
    schedulerStore.selectedTimelinePartnerOfficeIds = partnerOfficeIds;
  
    // Сохраняем в localStorage
    try {
      localStorage.setItem(
        LOCAL_STORAGE_KEYS.SELECTED_TIMELINE_PARTNER_OFFICE_IDS, 
        JSON.stringify(partnerOfficeIds)
      );
    } catch {
      // если не получилось сохранить, то ничего не делаем
    };
  }),

  loadSelectedTimelinePartnerOfficeIdsFromStorage: action(() => {
    try {
      const stored = localStorage.getItem(LOCAL_STORAGE_KEYS.SELECTED_TIMELINE_PARTNER_OFFICE_IDS);
      if (stored) {
        const parsedData = JSON.parse(stored);
        schedulerStore.selectedTimelinePartnerOfficeIds = parsedData;
      }
    } catch {
      schedulerStore.selectedTimelinePartnerOfficeIds = [];
    }
  }),

  setSelectedTimelineSchedulerClassifierRefValueId: action((schedulerClassifierRefValueId: number) => {
    schedulerStore.selectedTimelineSchedulerClassifierRefValueId = schedulerClassifierRefValueId;
  }),

  // setSelectedTimelineSinceDate: action((sinceDate: string) => {
  //   schedulerStore.selectedTimelineSinceDate = sinceDate;
  // }),

  // setSelectedTimelineTillDate: action((tillDate: string) => {
  //   schedulerStore.selectedTimelineTillDate = tillDate;
  // }),

  async getTimeline(request: TimelineRequestInterface) {
    schedulerActions.clearError();
    runInAction(() => {
      schedulerStore.timelineRequest = request;
      schedulerStore.loadingScheduler = true;
    });
    const response = await schedulerController.getTimeline(request);
    schedulerActions.handleError(response);
    runInAction(() => {
      schedulerStore.timeline = response.data;
      schedulerStore.loadingScheduler = false;
    });
  },

  async clearTimeline() {
    try {
      runInAction(() => {
        schedulerStore.timeline = {
          appointmentDefaultDuration: 30,
          appointmentGridStep: 30,
          startDayHour: 9,
          endDayHour: 18,
          appointments: [],
          nonWorkingHours: [],
          posts: [],
        };
        schedulerStore.timelineRequest = null;
      });
    } catch (err) {
      this.handleError(err);
    }
  },

};
